/**
 * ChatRail — multi-AP chat surface.
 *
 * Accepts apId prop and routes to POST /ap/chat with ap_id.
 * History rehydration from GET /ap/chat/history?ap_id=<apId>.
 * localStorage key: qms.ap_chat.<apId>.session_id
 * Migration: if apId='configuration', migrates the legacy key
 * qms.configuration_ap.session_id so existing chat history is preserved.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { getAccessToken } from "@/services/api/authSession"
import {
  fetchWithConnectivity,
  isAbortError,
} from "@/services/api/connectivity"
import { IconChevronRight, IconSend, IconSparkles } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useEffect, useRef, useState } from "react"
import { useApSimulationEngine } from "../../../lib/ap-simulation-engine"
import { useStore } from "../../../store/useStore"
import { refreshDbState } from "./refreshDbState"
import { seedImportDiff } from "./seedImportDiff"

const BASE =
  (import.meta.env.VITE_APP_API_BASE_URL ?? "http://localhost:8000/api/v1") +
  "/qms"

const LEGACY_CONFIGURATION_KEY = "qms.configuration_ap.session_id"

function storageKey(apId: string): string {
  return `qms.ap_chat.${apId}.session_id`
}

function getOrCreateApSessionId(apId: string): string {
  const key = storageKey(apId)
  // Migrate legacy configuration key
  if (apId === "configuration") {
    const legacy = localStorage.getItem(LEGACY_CONFIGURATION_KEY)
    if (legacy && !localStorage.getItem(key)) {
      localStorage.setItem(key, legacy)
      localStorage.removeItem(LEGACY_CONFIGURATION_KEY)
      return legacy
    }
  }
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

type ChunkKind = "message" | "receipt" | "action" | "error"

interface ChatChunk {
  kind: ChunkKind
  text?: string
  details?: Record<string, unknown>
  action?: { type: string } & Record<string, unknown>
}

interface ChatMessage {
  id: string
  role: "user" | "agent"
  kind: "message" | "receipt" | "error"
  text: string
  attachment_id?: string | null
  created_at?: string
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    "Accept-Language": acceptLanguage(),
  }
  try {
    headers.Authorization = `Bearer ${await getAccessToken()}`
  } catch (error) {
    // A request whose session has ended must not go out at all.
    if (isAbortError(error)) throw error
    // fall through
  }
  return headers
}

function acceptLanguage(): string {
  const lang = localStorage.getItem("language")?.toLowerCase()
  if (lang === "en" || lang === "de" || lang === "ja") return lang
  return "de"
}

export function ChatRail({ apId }: { apId: string }) {
  const { translate } = useTranslation()
  const {
    activeDiffSessionId,
    setActiveDiffSessionId,
    pendingChatAttachments,
    setPendingChatAttachments,
    pendingChatPrefill,
    setPendingChatPrefill,
    pendingChatQA,
    setPendingChatQA,
    pendingAgentBroadcast,
    setPendingAgentBroadcast,
  } = useStore()
  const sim = useApSimulationEngine(apId)
  const queryClient = useQueryClient()

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [composerText, setComposerText] = useState("")
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const apSessionIdRef = useRef<string>("")
  const qaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Dedup ref for broadcast processing — see the broadcast effect below.
  const processingBroadcastRef = useRef<unknown>(null)

  // Reset thread state when apId changes
  useEffect(() => {
    setMessages([])
    setComposerText("")
    setError(null)
  }, [apId])

  // Stable ap_session_id + history rehydration on apId change.
  useEffect(() => {
    const id = getOrCreateApSessionId(apId)
    apSessionIdRef.current = id
    ;(async () => {
      const url =
        `${BASE}/ap/chat/history` +
        `?ap_session_id=${encodeURIComponent(id)}&ap_id=${encodeURIComponent(apId)}`
      try {
        const auth = await getAuthHeaders()
        const res = await fetchWithConnectivity(url, { headers: auth })
        if (!res.ok) return
        const rows: ChatMessage[] = await res.json()
        setMessages(rows)
      } catch {
        // History is best-effort — empty on failure.
      }
    })()
  }, [apId])

  // Auto-scroll on new messages or during streaming.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isStreaming])

  const send = useCallback(
    async (text: string, files?: File[] | null) => {
      const trimmed = text.trim()
      const attachments = files ?? []
      if (!trimmed && attachments.length === 0) return
      if (isStreaming) return
      setComposerText("")
      setError(null)
      setIsStreaming(true)

      // Optimistically render the user turn.
      const attachmentLabel =
        attachments.length === 0
          ? null
          : attachments.length === 1
            ? attachments[0].name
            : translate("QMS/CHAT/ATTACHMENT_AND_MORE", {
                name: attachments[0].name,
                count: attachments.length - 1,
              })
      const localUserId = `local-user-${Date.now()}`
      setMessages((prev) => [
        ...prev,
        {
          id: localUserId,
          role: "user",
          kind: "message",
          text: trimmed,
          attachment_id: attachmentLabel,
        },
      ])

      // Frontend simulation engine intercept — for APs whose chat behaviour is
      // mocked entirely on the client, this skips the backend round-trip and
      // schedules a canned response via pendingAgentBroadcast. Document mode
      // (file uploads) always falls through to the backend so analysis still runs.
      if (attachments.length === 0 && sim.tryIntercept(trimmed)) {
        setIsStreaming(false)
        return
      }

      const form = new FormData()
      form.append("message", trimmed)
      form.append("ap_id", apId)
      form.append("ap_session_id", apSessionIdRef.current)
      if (activeDiffSessionId) {
        form.append("diff_session_id", activeDiffSessionId)
      }
      for (const f of attachments) {
        form.append("attachments", f)
      }

      try {
        const auth = await getAuthHeaders()
        const res = await fetch(`${BASE}/ap/chat`, {
          method: "POST",
          body: form,
          headers: auth,
        })
        if (!res.ok || !res.body) {
          throw new Error(`API ${res.status}: ${await res.text()}`)
        }
        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ""

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          let sep
          while ((sep = buf.indexOf("\n\n")) >= 0) {
            const raw = buf.slice(0, sep)
            buf = buf.slice(sep + 2)
            const line = raw.split("\n").find((l) => l.startsWith("data: "))
            if (!line) continue
            let chunk: ChatChunk
            try {
              chunk = JSON.parse(line.slice(6))
            } catch {
              continue
            }
            if (chunk.kind === "action" && chunk.action) {
              const action = chunk.action
              if (
                action.type === "open_session" &&
                typeof action.session_id === "string"
              ) {
                setActiveDiffSessionId(action.session_id)
                void refreshDbState(queryClient).then(() => {
                  const name = attachmentLabel ?? "Upload"
                  return seedImportDiff(name)
                })
              }
              if (action.type === "cancel_analysis") {
                setActiveDiffSessionId(null)
                useStore.getState().exitDiffMode()
              }
              if (action.type === "accept_all") {
                const state = useStore.getState()
                if (state.actionState) {
                  for (const scopeId of Object.keys(state.actionState.diffs)) {
                    state.acceptAllActionScopeDiffs(scopeId)
                  }
                }
                // Auto-approve the session regardless of actionState being
                // populated — avoids a race where seedImportDiff hasn't
                // finished fetching records yet when the user types early.
                useStore.setState({ _autoApproveSignal: Date.now() } as Partial<ReturnType<typeof useStore.getState>>)
              }
              continue
            }
            if (
              chunk.kind === "message" ||
              chunk.kind === "receipt" ||
              chunk.kind === "error"
            ) {
              const kind: "message" | "receipt" | "error" = chunk.kind
              setMessages((prev) => [
                ...prev,
                {
                  id: `local-agent-${Date.now()}-${prev.length}`,
                  role: "agent",
                  kind,
                  text: chunk.text ?? "",
                },
              ])
            }
          }
        }
      } catch (err: unknown) {
        // Abandoned, not failed: its session has ended.
        if (isAbortError(err)) return
        setError(
          err instanceof Error
            ? err.message
            : translate("QMS/CHAT/SEND_FAILED"),
        )
      } finally {
        setIsStreaming(false)
      }
    },
    [
      apId,
      activeDiffSessionId,
      isStreaming,
      queryClient,
      setActiveDiffSessionId,
      sim,
    ],
  )

  // Consume store signals: attachments from AktionenTab's dropzone, and
  // prefill text from seeded-prompt buttons.
  useEffect(() => {
    if (pendingChatAttachments.length > 0 && !isStreaming) {
      const files = pendingChatAttachments
      setPendingChatAttachments([])
      const message =
        files.length === 1
          ? translate("QMS/CHAT/ANALYZE_DOCUMENT")
          : translate("QMS/CHAT/ANALYZE_DOCUMENTS", { count: files.length })
      void send(message, files)
    }
  }, [pendingChatAttachments, isStreaming, send, setPendingChatAttachments])

  useEffect(() => {
    if (pendingChatPrefill != null) {
      setComposerText((curr) =>
        curr ? `${curr} ${pendingChatPrefill}` : pendingChatPrefill,
      )
      setPendingChatPrefill(null)
    }
  }, [pendingChatPrefill, setPendingChatPrefill])

  useEffect(() => {
    if (!pendingChatQA) return
    const { question, answer } = pendingChatQA
    setPendingChatQA(null)

    setMessages((prev) => [
      ...prev,
      {
        id: `qa-user-${Date.now()}`,
        role: "user",
        kind: "message",
        text: question,
      },
    ])
    setIsStreaming(true)

    if (qaTimerRef.current) clearTimeout(qaTimerRef.current)
    const delay = Math.min(1000 + answer.length * 6, 3000)
    qaTimerRef.current = setTimeout(() => {
      setIsStreaming(false)
      setMessages((prev) => [
        ...prev,
        {
          id: `qa-agent-${Date.now()}`,
          role: "agent",
          kind: "message",
          text: answer,
        },
      ])
      qaTimerRef.current = null
    }, delay)
  }, [pendingChatQA, setPendingChatQA])

  // Agent-only broadcasts from the simulation engine — sequential render with
  // per-message delays. Each message shows the typing indicator before it lands.
  //
  // Dedup-via-ref pattern: clearing pendingAgentBroadcast inside the effect
  // would change the dep, re-run the effect, and the cleanup would cancel
  // every scheduled timer before it fired. Instead, use the broadcast object
  // identity as a dedup key, clear the broadcast slot from the last timer
  // (so a new broadcast can land), and reset the ref in cleanup so React's
  // StrictMode (which re-runs effects) can re-schedule the timers.
  useEffect(() => {
    if (!pendingAgentBroadcast) return
    if (pendingAgentBroadcast.apId !== apId) return
    if (processingBroadcastRef.current === pendingAgentBroadcast) return
    processingBroadcastRef.current = pendingAgentBroadcast

    const broadcast = pendingAgentBroadcast
    const queue = broadcast.messages
    if (queue.length === 0) {
      setPendingAgentBroadcast(null)
      return
    }
    setIsStreaming(true)

    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []
    let cumulative = 0
    queue.forEach((msg, idx) => {
      cumulative += msg.delayMs ?? 1200
      const at = cumulative
      timers.push(
        setTimeout(() => {
          if (cancelled) return
          setMessages((prev) => [
            ...prev,
            {
              id: `bcast-${Date.now()}-${idx}`,
              role: "agent",
              kind: "message",
              text: msg.text,
            },
          ])
          if (idx === queue.length - 1) {
            setIsStreaming(false)
            // Clear the slot only AFTER the last message has rendered.
            setPendingAgentBroadcast(null)
          }
        }, at),
      )
    })
    return () => {
      cancelled = true
      for (const t of timers) clearTimeout(t)
      // Reset so a StrictMode re-run can re-process the same broadcast.
      processingBroadcastRef.current = null
    }
  }, [pendingAgentBroadcast, apId, setPendingAgentBroadcast])

  // Cancel any in-flight QA timer on unmount
  useEffect(
    () => () => {
      if (qaTimerRef.current) clearTimeout(qaTimerRef.current)
    },
    [],
  )

  return (
    <div className="flex h-full flex-col bg-grey1">
      <div className="flex items-center gap-1.5 border-b border-grey4 px-4 py-3">
        <IconSparkles size={16} className="text-yellow1" />
        <h3 className="text-sm font-semibold text-white">
          {translate("QMS/CHAT/ASSISTANT_TITLE")}
        </h3>
      </div>

      <div className="min-w-0 flex-1 space-y-2 overflow-y-auto overflow-x-hidden px-3 py-3">
        {messages.length === 0 && !isStreaming && (
          <p className="py-8 text-center text-sm text-grey5">
            {translate("QMS/CHAT/PLACEHOLDER")}
          </p>
        )}
        {messages.map((m) => (
          <ChatBubble key={m.id} message={m} />
        ))}
        {isStreaming && <TypingIndicator />}
        <div ref={chatEndRef} />
      </div>

      {error && (
        <div className="mx-3 mb-2 rounded border border-red1/30 bg-red1/10 p-2 text-xs text-red1">
          {error}
        </div>
      )}

      <div className="border-t border-grey4 bg-grey2 px-3 py-3">
        <div className="flex items-end gap-2">
          <textarea
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                void send(composerText)
              }
            }}
            placeholder={translate("QMS/CHAT/INPUT_PLACEHOLDER")}
            rows={1}
            disabled={isStreaming}
            className="flex-1 resize-none rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm text-white placeholder:text-grey5 focus:border-yellow1 focus:outline-hidden focus:ring-1 focus:ring-yellow1 disabled:opacity-50"
          />
          <button
            onClick={() => void send(composerText)}
            disabled={!composerText.trim() || isStreaming}
            className="rounded-md bg-yellow1 p-2 text-black transition-colors hover:bg-yellow5/60 disabled:cursor-not-allowed disabled:opacity-40"
            title={translate("QMS/CHAT/BTN_SEND_TITLE")}
          >
            <IconSend size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

function ChatBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user"
  if (message.kind === "receipt") {
    return <ReceiptRow text={message.text} />
  }
  if (message.kind === "error") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border border-red1/40 bg-red1/10 px-3 py-2 text-sm text-red1">
          {message.text}
        </div>
      </div>
    )
  }
  return (
    <div className={clsx("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={clsx(
          "max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-lg border px-3 py-2 text-sm wrap-anywhere",
          isUser
            ? "border-yellow5/40 bg-yellow5/20 text-white"
            : "border-grey4 bg-grey3 text-white",
        )}
      >
        {message.text}
        {message.attachment_id && (
          <div className="mt-1.5 wrap-break-word text-xs text-grey5 wrap-anywhere">
            📎 {message.attachment_id}
          </div>
        )}
      </div>
    </div>
  )
}

function ReceiptRow({ text }: { text: string }) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <button
      onClick={() => setOpen((v) => !v)}
      className="flex w-full items-center gap-2 rounded border border-grey4/60 bg-grey2/60 px-3 py-1.5 text-left text-xs text-grey5 hover:bg-grey3"
    >
      <IconChevronRight
        size={12}
        className={clsx("transition-transform", open && "rotate-90")}
      />
      <span className="flex-1 truncate">
        {text || translate("QMS/CHAT/RECEIPT_FALLBACK")}
      </span>
    </button>
  )
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-1 rounded-lg border border-grey4 bg-grey3 px-3 py-2">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-grey5 [animation-delay:0ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-grey5 [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-grey5 [animation-delay:300ms]" />
      </div>
    </div>
  )
}
